import React, { useState } from "react"
import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useNavigate, useLocation } from "react-router-dom";
import AppBarFile from '../containers/AppBarContainers/AppBar';
import AppBrand from '../containers/AppBarContainers/AppBrand';
import { useEffect } from 'react';
import SubMenu from './SubMenu';
import { SidebarData } from './SidebarData';
import { canOpen, homeFor, tabsFor } from './tabAccess';
import BillingBanner from '../containers/billing/BillingBanner';
import BillingGate from '../containers/billing/BillingGate';
import { useSelector } from "react-redux";
import "../App.css"
import { constants } from "../Helpers/constantsFile";

const drawerWidth = 260;

/**
 * Who may open what lives in tabAccess.js, so the sidebar and the redirect
 * guard below read the same list. A waiter gets the order pad, a cashier the
 * three counter screens, a storekeeper the store, a manager everything.
 */

const openedMixin = (theme) => ({
  width: drawerWidth,
  // background: "#6A03B6",
  background: constants.sideBarColor,
  // background: "#3A3A3A",
  border: "none",
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  border: "none",
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  background: "white",
  width: "65px",
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  background: "white",
  boxShadow: '2px 0px 4px -1px rgba(0,0,0,0.2), 4px 0px 5px 0px rgba(0,0,0,0.14), 1px 0px 10px 0px rgba(0,0,0,0.12)',
  color: "black",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,

    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);


export default function NewLayout({ children, props }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false)
  const [open, setOpen] = React.useState(false);
  const activeUser = useSelector(state => state.login?.activeUser)

  const role = activeUser?.role;

  /**
   * The tabs this role may see. Filtering here rather than in SidebarData
   * keeps that file a plain list of screens, with no idea who is looking at
   * it.
   */
  const tabs = tabsFor(role, SidebarData);

  /** Where this role starts: the order pad, or the store for a storekeeper. */
  const home = homeFor(role);

  useEffect(() => {
    navigate(home)
  }, [home])

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    // The Dashboard privilege only lands somebody there if their role may open
    // it at all — a cashier or a storekeeper carrying the privilege would
    // otherwise be thrown onto a screen the guard below immediately undoes.
    if (canOpen(role, "/dashboard") && activeUser?.privileges?.includes("Dashboard"))
      navigate("/dashboard")
  }, [navigate, role, activeUser])

  /**
   * Hiding the tabs is not the same as blocking the routes — a typed hash or
   * a stale history entry would still open the dashboard. This sends anybody
   * on a screen their role may not open back to their own home, replacing the
   * entry so Back doesn't bounce them in and out.
   */
  useEffect(() => {
    if (!canOpen(role, location.pathname)) {
      navigate(home, { replace: true });
    }
  }, [role, home, location.pathname, navigate])

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleShow = (location) => {
    if (location == "/") setShow(true)
    if (location !== "/") setShow(false)
  }

  const setNavigation = () => {
    navigate("/")
  }

  React.useEffect(() => {
    handleShow(location.pathname)
  }, [location])

  return (
    <div style={{
      display: "flex",
      width: "100%",
    }}>
      <AppBar position="fixed" open={open} style={{
        padding: '0px',
        margin: '0px',
        display: "flex",
        width: open ? `calc(100% - ${drawerWidth}px)` : "100%",
        flexDirection: "row",
        justifyContent: "space-between",
        background: "#FAFAFA",
        boxShadow: "none",
        borderBottom: "1px solid lightGrey"
      }}
      >
        {/* The left corner: the mark, the wordmark, and the shop.

            The Toolbar is what holds the bar open to its proper height — the
            brand alone left it 35px tall and clamped to the top of the screen.

            12.5px puts the 35px mark dead centre of the 65px rail: a tab row
            carries 10px of margin and 10px of padding, so its 20px icon is
            centred on 30, and so is this. The logo and the icons beneath it
            sit on one line down the left edge. */}
        <Toolbar style={{ paddingLeft: "12.5px", paddingRight: "0px" }}>
          <AppBrand />
        </Toolbar>

        {/* The monthly bill, when one is owed.

            A pill in the space that was already empty between the wordmark and
            the avatar, rather than a band across the screen: every screen below
            sizes itself against this bar (`calc(100vh - 90px)`), so making the
            bar taller would put a scrollbar on every tab in the app.

            It renders nothing at all when nothing is owed and nothing for a
            waiter, so with two children `space-between` behaves exactly as it
            did before. */}
        <BillingBanner />

        <AppBarFile open={open} setNavigation={setNavigation} />
      </AppBar>

      {/* Opens the payment dialog on sign-in and on every tab change. It draws
          nothing until then, and nothing ever for a waiter. */}
      <BillingGate />


      <Drawer variant="permanent" open={open} style={{display: "flex", width: "60px",
    margin: "0px"}}>

      {/* Empty on purpose: the logo lives in the app bar now. This is the
          spacer that pushes the tabs clear of it. */}
      <DrawerHeader />

        <div style={{ overflowY: "auto", height: "calc(100% - 120px)" }}>
        <List>
          {tabs.map((item, index) => {
            /* A hairline above the first tab of a group — never above the
               very first tab, which would draw a line under the logo. A
               waiter is filtered down to one tab and sees none of these. */
            return (
              <React.Fragment key={index}>
                {item.startsGroup && index > 0 ? (
                  <div style={{ height: "1px", background: "#E6E8EC", margin: "8px 14px" }} />
                ) : null}
                <SubMenu item={item} />
              </React.Fragment>
            )
          })}
        </List>
         </div>

      </Drawer>
      <div style={{
      width: "100%", background: "white",
margin: "100px auto 0",
        marginTop: "70px",
      }}>
        {location?.path == "/" && <Typography style = {{marginLeft:"20px",
      fontSize: "18px"}}> Hello {activeUser?.name}, welcome back!</Typography>}
        {children}
      </div>
    </div>
  );
}
